import React, { useState, useRef, useEffect } from "react";
import Pagination from "../Pagination";
import "./index.scss";
import { usePermissions } from "../../utils/roleUtils";

// ── Delete Confirmation Modal ─────────────────────────────────────────────────
const DeleteConfirmModal = ({
  isOpen,
  entity,
  onConfirm,
  onCancel,
  isDeleting,
}) => {
  if (!isOpen) return null;

  const entityName = entity?.entityName || entity?.EntityName || "this entity";
  const entityId = entity?.entityId || "";

  return (
    <div className="delete-modal-overlay" onClick={onCancel}>
      <div
        className="delete-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Icon */}
        <div className="delete-modal__icon">
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#ef4444"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
        </div>

        <h3 className="delete-modal__title">Delete Entity</h3>

        <p className="delete-modal__body">
          Are you sure you want to delete <strong>{entityName}</strong>
          {entityId && <span className="delete-modal__id"> ({entityId})</span>}?
        </p>

        <p className="delete-modal__warning">
          ⚠️ This action cannot be undone. All data associated with this entity
          will be permanently removed.
        </p>

        <div className="delete-modal__actions">
          <button
            type="button"
            className="delete-modal__btn delete-modal__btn--cancel"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="delete-modal__btn delete-modal__btn--confirm"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <span className="delete-modal__spinner" /> Deleting...
              </>
            ) : (
              "Yes, Delete"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── 3-dot action menu ─────────────────────────────────────────────────────────
const ActionMenu = ({
  row,
  onView,
  onEdit,
  onResend,
  onDelete,
  onLedger,
  onAddLedger,
}) => {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, right: 0 });
  const ref = useRef(null);
  const { canEdit, canResend, canDelete, isFinance } = usePermissions();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        setDropPos({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
        });
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setDropPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen((p) => !p);
  };

  return (
    <div className="action-menu" ref={ref}>
      <button
        className="action-menu__trigger"
        onClick={handleToggle}
        title="Actions"
      >
        ⋮
      </button>
      {open && (
        <div
          className="action-menu__dropdown"
          style={{
            position: "fixed",
            top: dropPos.top,
            right: dropPos.right,
            zIndex: 99999,
          }}
        >
          {/* View — always visible */}
          <button
            className="action-menu__item"
            onClick={() => {
              setOpen(false);
              onView(row);
            }}
          >
            View
          </button>
          {/* Edit — only for roles with canEdit, hidden when verified */}
          {canEdit && !row.entity?.isVerified && (
            <button
              className="action-menu__item"
              onClick={() => {
                setOpen(false);
                onEdit(row);
              }}
            >
              Edit
            </button>
          )}
          {/* Resend — for roles with canResend or Finance */}
          {(canResend || isFinance) && onResend && (
            <button
              className="action-menu__item"
              onClick={() => {
                setOpen(false);
                onResend(row);
              }}
            >
              Resend Link
            </button>
          )}
          {/* Ledger — opens ledger status modal */}
          {/* TODO: Option to update status with LedgerStatusModal is temporarily commented out
          {isFinance && (
            <button
              className="action-menu__item"
              onClick={() => {
                setOpen(false);
                onLedger(row);
              }}
            >
              Ledger
            </button>
          )}
          */}
          {/* Add New Ledger — visible when isLedger=yes AND isLedgerUploaded=true */}
          {/* {isFinance &&
            onAddLedger &&
            row.entity?.isLedger === "yes" &&
            row.entity?.isLedgerUploaded === true && (
              <button
                className="action-menu__item"
                onClick={() => {
                  setOpen(false);
                  onAddLedger(row);
                }}
              >
                Add New Ledger
              </button>
            )} */}
          {/* Delete — only for roles with canDelete, hidden when verified */}
          {canDelete && onDelete && !row.entity?.isVerified && (
            <button
              className="action-menu__item action-menu__item--danger"
              onClick={() => {
                setOpen(false);
                onDelete(row);
              }}
            >
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ── Table ─────────────────────────────────────────────────────────────────────
const Table = ({
  columns,
  data,
  onEdit,
  onDelete,
  onResend,
  onLedger,
  onAddLedger,
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  showPagination = false,
  showPaginationInfo = true,
  expandedRow,
  // selection props (Finance only)
  selectable = false,
  selectedIds = new Set(),
  onSelectRow,
  onSelectAll,
  getRowId,
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null); // row to delete
  const [isDeleting, setIsDeleting] = useState(false);

  const toggleRow = (index) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  const handleDeleteClick = (row) => setDeleteTarget(row);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || !onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(deleteTarget);
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleDeleteCancel = () => {
    if (!isDeleting) setDeleteTarget(null);
  };

  // Only rows where ledger is required AND not yet uploaded are selectable
  const ledgerSelectableRows = selectable
    ? data.filter((row) => {
        const ledgerRequired = row.entity?.isLedger === "yes";
        const ledgerUploaded =
          row.entity?.isLedgerUploaded === true ||
          row.entity?.isLedgerUploaded === "yes";
        return ledgerRequired && !ledgerUploaded;
      })
    : [];

  const allSelected =
    selectable &&
    ledgerSelectableRows.length > 0 &&
    ledgerSelectableRows.every((row) => selectedIds.has(getRowId?.(row)));
  const someSelected =
    selectable &&
    ledgerSelectableRows.some((row) => selectedIds.has(getRowId?.(row)));

  return (
    <div className="table-container">
      {/* Delete confirmation modal */}
      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        entity={deleteTarget?.entity}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isDeleting={isDeleting}
      />
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              {selectable && (
                <th className="th-checkbox">
                  <input
                    type="checkbox"
                    className="row-checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected && !allSelected;
                    }}
                    onChange={(e) =>
                      onSelectAll?.(e.target.checked, ledgerSelectableRows)
                    }
                    title={
                      allSelected
                        ? "Deselect all"
                        : "Select all ledger-required"
                    }
                    disabled={ledgerSelectableRows.length === 0}
                  />
                </th>
              )}
              {columns.map((col, i) => (
                <th key={i}>{col.header}</th>
              ))}
              {onEdit && <th className="th-actions">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {data && data.length > 0 ? (
              data.map((row, rowIndex) => {
                const rowId = getRowId?.(row);
                const isSelected = selectable && selectedIds.has(rowId);

                return (
                  <React.Fragment key={rowIndex}>
                    <tr
                      className={isSelected ? "tr--selected" : ""}
                      onClick={
                        expandedRow ? () => toggleRow(rowIndex) : undefined
                      }
                      style={expandedRow ? { cursor: "pointer" } : undefined}
                    >
                      {selectable && (
                        <td
                          className="td-checkbox"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {(() => {
                            const ledgerRequired =
                              row.entity?.isLedger === "yes";
                            const ledgerUploaded =
                              row.entity?.isLedgerUploaded === true ||
                              row.entity?.isLedgerUploaded === "yes";

                            // Determine disabled reason for tooltip
                            const disabledReason = !ledgerRequired
                              ? "Ledger not required"
                              : ledgerUploaded
                                ? "Ledger already uploaded"
                                : null;

                            const isDisabled = !!disabledReason;

                            return (
                              <span
                                title={disabledReason || undefined}
                                style={
                                  isDisabled
                                    ? {
                                        cursor: "not-allowed",
                                        display: "inline-block",
                                      }
                                    : undefined
                                }
                              >
                                <input
                                  type="checkbox"
                                  className="row-checkbox"
                                  checked={isSelected}
                                  disabled={isDisabled}
                                  style={
                                    isDisabled
                                      ? {
                                          cursor: "not-allowed",
                                          opacity: 0.35,
                                          pointerEvents: "none",
                                        }
                                      : undefined
                                  }
                                  onChange={(e) =>
                                    !isDisabled &&
                                    onSelectRow?.(rowId, row, e.target.checked)
                                  }
                                />
                              </span>
                            );
                          })()}
                        </td>
                      )}
                      {columns.map((col, colIndex) => (
                        <td key={colIndex}>
                          {col.render ? col.render(row) : row[col.accessor]}
                        </td>
                      ))}
                      {onEdit && (
                        <td
                          className="td-actions"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ActionMenu
                            row={row}
                            onView={(r) => onEdit(r, true)}
                            onEdit={(r) => onEdit(r, false)}
                            onResend={onResend}
                            onDelete={onDelete ? handleDeleteClick : undefined}
                            onLedger={onLedger}
                            onAddLedger={onAddLedger}
                          />
                        </td>
                      )}
                    </tr>
                    {expandedRow && expandedRows.has(rowIndex) && (
                      <tr className="expanded-row">
                        <td
                          colSpan={
                            columns.length +
                            (onEdit ? 1 : 0) +
                            (selectable ? 1 : 0)
                          }
                        >
                          {expandedRow(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={
                    columns.length + (onEdit ? 1 : 0) + (selectable ? 1 : 0)
                  }
                  className="no-data"
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="table-pagination">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            showInfo={showPaginationInfo}
          />
        </div>
      )}
    </div>
  );
};

export default Table;
